import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsBoxSeam, BsCreditCard, BsHeadset, BsPuzzle } from 'react-icons/bs';

import AddonCard from '#globalComponents/addons/AddonCard';
import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import SectionCard from '#globalComponents/Card/SectionCard';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import Tag from '#globalComponents/Tag/Tag';
import { useAuth } from '#globalContexts/AuthProvider';
import { CRM_ADDON, PHONE_ADDON, isAddonManageable } from '#lib/addons/addonConfigs';
import { formatCents } from '#lib/numberFormat';

import { FULLSERVICE_BOOKING_URL } from '../../constants/bookingLinks';
import { useBillingStatus } from '../../hooks/useBillingStatus';
import { usePlanSelect } from '../../models/billing/mutations';
import { useUserSubscription, useNextUpgradePlan } from '../../models/billing/queries';

import { BillingPortalLink } from './components/BillingPortalLink/BillingPortalLink';
import { CurrentPlanHeader } from './components/CurrentPlanHeader/CurrentPlanHeader';
import { ManageSubscriptionModal } from './components/ManageSubscriptionModal/ManageSubscriptionModal';
import { PlanSelectionCard } from './components/PlanSelectionCard/PlanSelectionCard';
import { SubscriptionStatusNotice } from './components/SubscriptionStatusNotice/SubscriptionStatusNotice';

const ADDONS = [CRM_ADDON, PHONE_ADDON];

const PlansSection = ({ subscription, isLocked }) => (
	<section>
		<div className="mb-3 flex items-center gap-2.5 px-1">
			<BsBoxSeam className="h-4 w-4 flex-shrink-0 text-primary-500" />
			<div>
				<h2 className="text-sm-bold text-text">Pakete</h2>
				<p className="text-xs text-bgBase-800">Sorgen Sie sich nie mehr, woher der nächste Auftrag kommt.</p>
			</div>
		</div>
		<PlanSelectionCard currentSubscription={subscription} isLocked={isLocked} />
	</section>
);

PlansSection.propTypes = {
	subscription: PropTypes.object,
	isLocked: PropTypes.bool.isRequired,
};

const FullserviceSection = () => (
	<SectionCard title="Fullservice" subtitle="Optional" icon={BsHeadset}>
		<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
			<p className="max-w-2xl text-sm text-bgBase-800">
				Wir übernehmen auf monatlicher Basis Ihre Akquise. Wir suchen die richtigen Kontakte für Sie raus und
				übernehmen die Versendung der Mails bis hin zur telefonischen Akquise. Umfang und Kosten nach Absprache.
			</p>
			<Button variant="solid" href={FULLSERVICE_BOOKING_URL} target="_blank" className="shrink-0">
				Fullservice anfragen
			</Button>
		</div>
	</SectionCard>
);

export const BillingSettings = () => {
	const { orgPlan } = useAuth();
	const { data: subscription, isLoading } = useUserSubscription();
	const { view, isPaused, needsPaymentUpdate } = useBillingStatus();
	const { data: nextPlan, isLoading: nextPlanLoading } = useNextUpgradePlan(subscription);
	const planSelectMutation = usePlanSelect(subscription);
	const [isUpgradeConfirmOpen, setIsUpgradeConfirmOpen] = useState(false);
	const [manageModal, setManageModal] = useState(null);

	const isLocked = view !== 'active' && view !== 'none';
	// Without `retention` in the payload the backend cannot say, so the button stays available.
	const canCancel = subscription?.retention ? subscription.retention.canCancel : true;
	const isOnTopPlan = view === 'active' && !nextPlanLoading && !nextPlan;
	const manageableAddons = ADDONS.filter((addon) => isAddonManageable(addon, orgPlan));
	// The wizard works on a snapshot: the live query is rewritten mid-mutation (before mutateAsync
	// resolves), and the steps must keep showing the state the customer started from.
	const openManageModal = (initialStep) => setManageModal({ initialStep, subscription });

	const handleUpgradeConfirm = () => planSelectMutation.mutateAsync(nextPlan.monthlyPrice.stripePriceId);

	if (isLoading) {
		return (
			<SectionCard title="Ihr Abonnement" icon={BsCreditCard}>
				<div className="flex flex-col items-center py-12">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
					<p className="mt-4 text-sm text-bgBase-800">Lade Abonnement-Informationen...</p>
				</div>
			</SectionCard>
		);
	}

	return (
		<div className="space-y-5">
			{subscription && (
				<Card className="overflow-hidden p-0">
					<CurrentPlanHeader
						planName={subscription.stripePrices?.products?.name || 'Aktueller Plan'}
						planPriceCents={subscription.stripePrices?.unitAmount ?? 0}
						planDescription={subscription.stripePrices?.products?.shortDescription}
						badge={isOnTopPlan && <Tag tone="primary" label="Höchster Tarif" />}
					/>

					<div className="flex flex-col gap-4 p-5">
						{isLocked && (
							<SubscriptionStatusNotice
								subscription={subscription}
								view={view}
								isPaused={isPaused}
								onCancelAnyway={() => openManageModal('reason')}
								onPauseInstead={() => openManageModal('pause')}
							/>
						)}

						<div className="flex flex-wrap items-center gap-3">
							{view === 'active' && nextPlan && (
								<Button
									variant="solid"
									onClick={() => setIsUpgradeConfirmOpen(true)}
									loading={planSelectMutation.isPending}
									disabled={!nextPlan.monthlyPrice}
								>
									{`Auf ${nextPlan.name} upgraden`}
								</Button>
							)}
							{view === 'active' && canCancel && (
								<Button variant="outline" onClick={() => openManageModal('reason')}>
									Abonnement kündigen oder pausieren
								</Button>
							)}
							{view === 'active' && !canCancel && needsPaymentUpdate && (
								<p className="text-sm text-bgBase-800">
									Bitte klären Sie zuerst die offene Zahlung, bevor Sie Ihr Abonnement kündigen oder
									pausieren.
								</p>
							)}
							<BillingPortalLink className="sm:ml-auto" />
						</div>
					</div>
				</Card>
			)}

			<PlansSection subscription={subscription} isLocked={isLocked} />

			{manageableAddons.length > 0 && (
				<SectionCard title="Add-ons" icon={BsPuzzle}>
					<div className="divide-y divide-bgBase-500">
						{manageableAddons.map((addon) => (
							<AddonCard key={addon.key} addon={addon} />
						))}
					</div>
				</SectionCard>
			)}

			<FullserviceSection />

			<ConfirmModal
				isOpen={isUpgradeConfirmOpen}
				onClose={() => setIsUpgradeConfirmOpen(false)}
				onConfirm={handleUpgradeConfirm}
				title="Upgrade bestätigen"
				message={
					nextPlan
						? `Möchten Sie Ihr Abonnement auf den Plan „${nextPlan.name}“ (${formatCents(nextPlan.monthlyPrice.unitAmount)}/Monat) umstellen?`
						: ''
				}
				subtitle="Die Umstellung wird sofort wirksam und Ihre Abrechnung entsprechend angepasst."
				confirmText="Plan wechseln"
				color="primary"
				isLoading={planSelectMutation.isPending}
			/>

			{manageModal && (
				<ManageSubscriptionModal
					onClose={() => setManageModal(null)}
					subscription={manageModal.subscription}
					initialStep={manageModal.initialStep}
				/>
			)}
		</div>
	);
};
